import { Component, OnInit, ChangeDetectionStrategy, Inject } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { combineLatest, Observable } from 'rxjs';
import { map, startWith, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { AffiliateWebsite, MerchantAd } from '../../../../core/dtos/api';
import { Clipboard } from '@angular/cdk/clipboard';
import { AffiliateApiService } from '../../../../core/services/api/affiliate-api.service';
import { stripHttpWWWFromUrl } from '../../../../core/pure-utils/string-utils';
import { outPlaceSort } from '../../../../core/pure-utils/array-utils';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DashboardApiService } from '../../../../core/services/api/dashboard-api.service';
import { SnackBarService } from '../../../../core-services/snack-bar/snack-bar.service';
import { AdManagementService } from '../../ad-management.service';
import { patternValidator } from '../../../../core/pure-utils/validators';

type LinkType = 'dynamicJavaScript' | 'html' | 'link';

@Component({
    templateUrl: './get-link-dialog.component.html',
    styleUrls: ['./get-link-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class GetLinkDialogComponent implements OnInit {

  constructor(public dialogRef: MatDialogRef<GetLinkDialogComponent>, @Inject(MAT_DIALOG_DATA) public merchantAd: MerchantAd,
    private fb: UntypedFormBuilder, private clipboard: Clipboard,
    private affiliateApiService: AffiliateApiService, private dashboardApiService: DashboardApiService,
    private snackBarService: SnackBarService, private adManagementService: AdManagementService) {}

  //imageLink = '';
  currentAccount$ = this.dashboardApiService.currentAccount$;

  linkTypeMap = {
    dynamicJavaScript: { label: 'Dynamic Javascript', linkLabel: 'Dynamic JS'},
    html: { label: 'HTML', linkLabel: 'HTML' },
    link: { label: 'Link', linkLabel: 'Tracking Link' }
  };
  readonly ctcValidPattern = /^[A-Za-z\d\.| \-\_~]*$/;

  linkTypeEntries = Object.entries(this.linkTypeMap);

  controls = {
    linkType: this.fb.control('dynamicJavaScript'),
    affiliateWebsite: this.fb.control({}),
    customTrackingCode: this.fb.control('',
      [Validators.maxLength(64), patternValidator(this.ctcValidPattern, 'Must be alphanumeric, a period, space, hyphen, pipe, tilde, or underscore.')]),
    openLinkInNewWindow: this.fb.control(false)
  };

  controlValues = {
    linkType$: this.controls.linkType.valueChanges.pipe(startWith<LinkType>(this.controls.linkType.value)),
    affiliateWebsite$: this.controls.affiliateWebsite.valueChanges.pipe(startWith<string>(this.controls.affiliateWebsite.value)),
    customTrackingCode$: this.controls.customTrackingCode.valueChanges.pipe(startWith<string>(this.controls.customTrackingCode.value)),
    openLinkInNewWindow$: this.controls.openLinkInNewWindow.valueChanges.pipe(startWith<boolean>(this.controls.openLinkInNewWindow.value)),
  };

  affiliateWebsites$ = this.currentAccount$.pipe(
    switchMap(x => this.affiliateApiService.fetchAffiliateWebsites(x.entity_id)),
    map(affiliateWebsites => {
      let withStrippedUrl = affiliateWebsites.map(x => ({...x, affiliateWebsiteUrlStripped: stripHttpWWWFromUrl(x.affiliate_website_url)}));
      return outPlaceSort(withStrippedUrl, x => x.affiliateWebsiteUrlStripped);
    }),
    tap(affiliateWebsites => this.controls.affiliateWebsite.setValue(affiliateWebsites.find(x => x.is_default)))
  );

  trackingLink$ = combineLatest([this.controlValues.linkType$, <Observable<AffiliateWebsite>> this.controls.affiliateWebsite.valueChanges,
    this.controlValues.customTrackingCode$, this.controlValues.openLinkInNewWindow$, this.currentAccount$]).pipe(
      map(([linkType, affiliateWebsite, customTrackingCode, openLinkInNewWindow, currentAccount]) => {
        /* eslint-disable max-len */
        customTrackingCode = encodeURIComponent(customTrackingCode.trim());

        //this.imageLink = `${environment.classicUrl}/gbi/${this.merchantAd.merchant.merchant_id}/${this.merchantAd.merchant_ad_id}/${currentAccount.entity_id}/${affiliateWebsite.affiliate_website_id}/image.png${customTrackingCode && '?custom_tracking_code=' + customTrackingCode}`;

        if (linkType === 'dynamicJavaScript') {
          return `<script type="text/javascript" src="${environment.classicUrl}/link.php?merchant_link_id=${this.merchantAd.merchant_ad_id}&amp;affiliate_id=${currentAccount.entity_id}&amp;website_id=${affiliateWebsite.affiliate_website_id}${openLinkInNewWindow ? '&amp;open=_blank' : ''}${customTrackingCode && '&amp;custom_tracking_code=' + customTrackingCode}"></script>`;
        }

        if (linkType === 'html') {
          let anchorOpenTag = `<a href="${environment.classicUrl}/click.php?tool_type=ml&amp;merchant_link_id=${this.merchantAd.merchant_ad_id}&amp;website_id=${affiliateWebsite.affiliate_website_id}${customTrackingCode && '&amp;custom_tracking_code=' + customTrackingCode}"${openLinkInNewWindow ? ' target=_blank' : ''} rel="sponsored">`;

          if (this.merchantAd.ad_type === 'text') {
            return anchorOpenTag + `${this.merchantAd.link_text}<img src="${environment.classicUrl}/tpv/${this.merchantAd.merchant.merchant_id}/${this.merchantAd.merchant_ad_id}/${currentAccount.entity_id}/${affiliateWebsite.affiliate_website_id}/${customTrackingCode === '' ? '-' : customTrackingCode}/ml/image.png" width="0" height="0" style="visibility: hidden; border: none !important; margin: 0px !important;" alt="" /></a>`;
          } else {
            return anchorOpenTag + `<img src="${environment.classicUrl}/gbi/${this.merchantAd.merchant.merchant_id}/${this.merchantAd.merchant_ad_id}/${currentAccount.entity_id}/${affiliateWebsite.affiliate_website_id}/image.png${customTrackingCode && '?custom_tracking_code=' + customTrackingCode}" width="${this.merchantAd.image_width}" height="${this.merchantAd.image_height}" style="border: 0px;" alt="" /></a>`;
          }
        }

        if (linkType === 'link') {
          return `${environment.classicUrl}/click.php?tool_type=ml&merchant_link_id=${this.merchantAd.merchant_ad_id}&website_id=${affiliateWebsite.affiliate_website_id}${customTrackingCode && '&custom_tracking_code=' + customTrackingCode}`;
        }

        return '';
        /* eslint-enable max-len */
      })
  );

  onCopyClick(trackingUrl: string) {
    this.clipboard.copy(trackingUrl);
    this.snackBarService.openSnackBar('Copied');
    this.dialogRef.close();
  }

  onDownloadClick() {
    this.adManagementService.downloadAdImage(this.merchantAd).subscribe();
  }

  // workaround for expansion panel FOUC in dialog, https://github.com/angular/components/issues/13870
  disableAnimation = true;
  ngOnInit() {
    setTimeout(() => this.disableAnimation = false);
    this.controls.customTrackingCode.markAsTouched();
  }
}
